/**
 * Minimum ConnectorAccess per Engine method (engine.md §15 "Access levels", connector.md). The engine
 * checks it on every call; the MCP host checks the tool level first.
 *
 *   read      every read, plus pipeline work (automatic, not the caller's intent: the MCP host runs
 *             pipeline.runPending before serving any tool)
 *   organise  message commands, digests, corrections, runNow, harvest/push, undo — and tags.create,
 *             because the organise-level MCP tool `tag_messages` creates unknown tags (connector.md)
 *   full      every other create / update / archive / reorder / settings / plan / sync / workspace write
 */

import type { Engine } from '../contracts/engine.js';
import type { ConnectorAccess } from '../contracts/entities.js';

/** Every method of the Engine interface as a dotted path ('messages.list', 'sources.telegram.sendCode', 'undo'). */
export type MethodPaths<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends (...args: never[]) => unknown ? `${P}${K}` : T[K] extends object ? MethodPaths<T[K], `${P}${K}.`> : never;
}[keyof T & string];

export type EnginePath = MethodPaths<Engine>;

/** Typed as a complete Record over EnginePath: a method added to the Engine contract fails to compile here. */
export const METHOD_ACCESS: Readonly<Record<EnginePath, ConnectorAccess>> = {
  'messages.list': 'read',
  'messages.get': 'read',
  'messages.open': 'read',
  'messages.counts': 'read',
  'messages.markdown': 'read',
  'messages.move': 'organise',
  'messages.setRead': 'organise',
  'messages.setTopics': 'organise',
  'messages.setTags': 'organise',
  'messages.tag': 'organise',
  'messages.delete': 'organise',
  'messages.setAssessment': 'organise',
  'messages.rescore': 'organise',

  'views.list': 'read',
  'views.preview': 'read',
  'views.create': 'full',
  'views.update': 'full',
  'views.delete': 'full',
  'views.reorder': 'full',

  'folders.list': 'read',
  'folders.create': 'full',
  'folders.update': 'full',
  'folders.archive': 'full',
  'folders.reorder': 'full',

  'tags.list': 'read',
  'tags.create': 'organise',
  'tags.update': 'full',
  'tags.delete': 'full',
  'tags.reorder': 'full',

  'topics.list': 'read',
  'topics.create': 'full',
  'topics.update': 'full',
  'topics.archive': 'full',
  'topics.restore': 'full',
  'topics.reorder': 'full',

  'sources.list': 'read',
  'sources.get': 'read',
  'sources.resolve': 'read',
  'sources.status': 'read',
  'sources.stats': 'read',
  'sources.overview': 'read',
  'sources.defaults': 'read',
  'sources.test': 'read',
  'sources.availability': 'read',
  'sources.create': 'full',
  'sources.update': 'full',
  'sources.setEnabled': 'full',
  'sources.archive': 'full',
  'sources.restore': 'full',
  'sources.remove': 'full',
  'sources.harvestNow': 'organise',
  'sources.push': 'organise',
  'sources.telegram.authState': 'read',
  'sources.telegram.dialogs': 'read',
  'sources.telegram.sendCode': 'full',
  'sources.telegram.submitCode': 'full',
  'sources.telegram.submitPassword': 'full',
  'sources.telegram.signOut': 'full',

  'rules.list': 'read',
  'rules.test': 'read',
  'rules.create': 'full',
  'rules.update': 'full',
  'rules.archive': 'full',
  'rules.restore': 'full',
  'rules.reorder': 'full',
  'rules.runNow': 'organise',
  'rules.resetLadder': 'full',

  'corrections.list': 'read',
  'corrections.resolve': 'organise',

  'digests.list': 'read',
  'digests.get': 'read',
  'digests.save': 'organise',
  'digests.delete': 'organise',

  'settings.get': 'read',
  'settings.getLocal': 'read',
  'settings.set': 'full',
  'settings.setLocal': 'full',

  'plan.entitlement': 'read',
  'plan.usage': 'read',
  'plan.check': 'read',
  'plan.setEntitlement': 'full',

  'pipeline.tick': 'read',
  'pipeline.runPending': 'read',
  'pipeline.status': 'read',

  'sync.status': 'read',
  'sync.devices': 'read',
  'sync.start': 'full',
  'sync.stop': 'full',
  'sync.now': 'full',
  'sync.revokeDevice': 'full',

  'models.catalog': 'read',
  'models.list': 'read',
  'models.get': 'read',
  'models.test': 'read',
  'models.installs': 'read',
  'models.rescore': 'organise',
  'models.update': 'full',
  'models.reorder': 'full',
  'models.applyRecommended': 'full',
  'models.download': 'full',
  'models.pauseDownload': 'full',
  'models.cancelDownload': 'full',
  'models.removeDownload': 'full',

  'workspace.export': 'read',
  'workspace.stats': 'read',
  'workspace.importSeed': 'full',
  'workspace.reset': 'full',

  undo: 'organise',
  subscribe: 'read',
  close: 'read',
};
